/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { Dropdown, DropdownSkeleton } from 'carbon-components-react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { sharedMessages as messages } from '../../messages';

import './ControlledDropdownSelector.scss';

const ControlledDropdownSelector = props => {
  const {
    customizationIdField,
    id,
    field,
    defaultValue,
    displayProp,
    itemProp,
    items,
    helperText,
    labelText,
    readOnly,
    enableNone,
    onChange,
    instanceLoaded
  } = props;

  const [index, setIndex] = useState();

  // We copy list because we might modify it.
  const [itemList, setItemList] = useState([]);

  const intl = useIntl();

  const formMethods = useFormContext();
  const { trigger } = formMethods;
  const { getFieldValue, isValid } = useFormHelpers(formMethods);

  const NONE = intl.formatMessage(messages.none);

  // Add None to Item List
  const updateItemList = useCallback(() => {
    let modifiedList = [];
    if (items) {
      modifiedList = [...items];
      if (enableNone) {
        // Add a None option to the top of Items if enabled.
        modifiedList.unshift({
          [displayProp]: NONE,
          [itemProp]: null
        });
      }
    }
    return modifiedList;
  }, [NONE, displayProp, enableNone, itemProp, items]);

  // Determine the defaultItem
  const getDefaultItem = useCallback(() => {
    // If the field is set, use it.
    const fieldValue = getFieldValue(field);
    const value =
      !Boolean(fieldValue) && enableNone
        ? NONE
        : field
        ? fieldValue
        : defaultValue;
    return {
      [displayProp]: value,
      [itemProp]: value
    };
  }, [
    NONE,
    defaultValue,
    displayProp,
    enableNone,
    field,
    getFieldValue,
    itemProp
  ]);

  // Find the default Item
  const findDefaultItem = useCallback(
    (item, list) => {
      if (item && list.length > 0) {
        return item[itemProp]
          ? list.findIndex(m => m[itemProp] === item[itemProp])
          : 0;
      }
    },
    [itemProp]
  );

  // When Field & Items change, update everything.
  useEffect(() => {
    const defaultItem = getDefaultItem();
    const list = updateItemList();
    const index = findDefaultItem(defaultItem, list);
    setItemList(prevList => (_.isEqual(prevList, list) ? prevList : list));
    setIndex(prevIndex => (prevIndex !== index ? index : prevIndex));
  }, [field, items, findDefaultItem, getDefaultItem, updateItemList]);

  useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <>
      {instanceLoaded &&
      itemList &&
      itemList.length > 0 &&
      typeof index !== 'undefined' ? (
        // If field, use a Controlled version.
        <Dropdown
          id={id}
          direction={'top'}
          readOnly={readOnly}
          items={itemList}
          titleText={labelText}
          hideLabel
          label={labelText}
          helperText={helperText}
          invalid={!isValid(field)}
          initialSelectedItem={itemList[index]}
          selectedItem={
            customizationIdField
              ? itemList.filter(
                  item => item.customization_id === getFieldValue(field)
                )[0] || ''
              : itemList.filter(
                  item => item.name === getFieldValue(field)
                )[0] || ''
          }
          itemToString={item =>
            item[displayProp] ?? item?.friendly_name ?? item?.name ?? ''
          }
          onChange={({ selectedItem }) => {
            onChange(selectedItem);
            trigger();
          }}
        />
      ) : (
        <DropdownSkeleton />
      )}
      <div className="TabContent__spacer32" />
    </>
  );
};

ControlledDropdownSelector.propTypes = {
  customizationIdField: PropTypes.bool,
  id: PropTypes.string.isRequired,
  field: PropTypes.string,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  defaultValue: PropTypes.string,
  filteredItems: PropTypes.array,
  displayProp: PropTypes.string,
  itemProp: PropTypes.string,
  enableNone: PropTypes.bool,
  labelText: PropTypes.string.isRequired,
  titleText: PropTypes.string,
  helperText: PropTypes.string,
  readOnly: PropTypes.bool,
  instanceLoaded: PropTypes.bool
};

ControlledDropdownSelector.defaultProps = {
  customizationIdField: false,
  readOnly: false,
  displayProp: 'friendly_name',
  itemProp: 'name',
  instanceLoaded: false
};

export { ControlledDropdownSelector };
